import { useMemo } from 'react';
import type { JSONContent } from '@tiptap/core';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { translateDocumentPartial } from './translatedDocument';

const QUERY_STALE_TIME = 30_000;

// Loads a language's published translation and overlays it per segment onto the
// draft (machine-only). Segments that fall back to English are flagged and
// counted in `editedCount`. `translatedContent` is null only while loading, on
// error, or with no language selected.
export function useUnitGuideTranslation({
  content,
  resourceVersionId,
  language,
  enabled,
}: {
  content: JSONContent;
  resourceVersionId: number;
  language: string | null;
  enabled: boolean;
}): {
  isLoading: boolean;
  isError: boolean;
  translatedContent: JSONContent | null;
  editedCount: number;
  translatedCount: number;
} {
  const active = enabled && language != null;

  const segmentsQuery = trpcAdmin.translationOverrides.extractSegments.useQuery(
    { resourceVersionId, language: language ?? '' },
    { enabled: active, staleTime: QUERY_STALE_TIME },
  );

  const partial = useMemo(() => {
    const segments = segmentsQuery.data?.segments;
    if (!active || segments === undefined) {
      return null;
    }
    return translateDocumentPartial(content, segments);
  }, [active, content, segmentsQuery.data?.segments]);

  return {
    isLoading: active && segmentsQuery.isLoading,
    isError: active && segmentsQuery.isError,
    translatedContent: partial?.content ?? null,
    editedCount: partial?.editedCount ?? 0,
    translatedCount: partial?.translatedCount ?? 0,
  };
}
